/**
 * Renders parsed Markdown blocks as React elements.
 *
 * Elements, never HTML strings: model output goes through parseMarkdown and
 * comes out as data, so there is no innerHTML pathway for it to ride in on.
 *
 * Headings map down two levels — the recap's h1 lands as an h3 — because
 * this always renders inside a card whose own title is the h2 of the page.
 */
import type { JSX } from 'react';

import type { Block, Inline } from '@/md/parse';
import { parseMarkdown } from '@/md/parse';

function InlineRun({ nodes }: { nodes: Inline[] }) {
  return (
    <>
      {nodes.map((n, i) => {
        switch (n.kind) {
          case 'strong':
            return (
              <strong key={i}>
                <InlineRun nodes={n.children} />
              </strong>
            );
          case 'em':
            return (
              <em key={i}>
                <InlineRun nodes={n.children} />
              </em>
            );
          case 'code':
            return <code key={i}>{n.text}</code>;
          default:
            return n.text;
        }
      })}
    </>
  );
}

function BlockNode({ block }: { block: Block }) {
  switch (block.kind) {
    case 'heading': {
      const Tag = `h${Math.min(block.level + 2, 6)}` as keyof JSX.IntrinsicElements;
      return (
        <Tag>
          <InlineRun nodes={block.children} />
        </Tag>
      );
    }
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag>
          {block.items.map((item, i) => (
            <li key={i}>
              <InlineRun nodes={item} />
            </li>
          ))}
        </Tag>
      );
    }
    default:
      return (
        <p>
          <InlineRun nodes={block.children} />
        </p>
      );
  }
}

export function Markdown({ md }: { md: string }) {
  return (
    <div className="md">
      {parseMarkdown(md).map((block, i) => (
        <BlockNode key={i} block={block} />
      ))}
    </div>
  );
}
